import React, { useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import type { ProcedimientoVista } from '@api/Consents/Consent';
import { ConsentApi, ModoConAcceso } from '@api/Consents/ConsentApi';
import SignatureField, { SignatureFieldHandle } from '@src/components/SignatureField';
import { conPunto, esRenglonDeHoy, fechaHora, pesos } from '@src/shared/utils/consent';
import { AvisoSnackbar, Cabecera, Cargando, Dato, ErrorCarga, Listo, Seccion } from './common';
import { FirmaConsentimiento } from './Consentimiento';
import { useExpediente } from './useExpediente';

const DECLARACION_DEFAULT =
    'Acepto el diseño que se me realizó previo a su ejecución, confirmo que la información de salud que compartí con Kabandú sigue vigente y autorizo el procedimiento descrito en este renglón.';

/**
 * Estación de firma: lo que abre el QR que el Punto de Venta le muestra a la clienta (link de 2 h,
 * `?staff=` + `&order=`), en su teléfono o en la tablet. Solo FIRMAS, en orden: su contrato si aún
 * no lo firmó y, después, el procedimiento de ese pedido. Lo demás (historial clínico, desechable,
 * pigmento, observaciones) lo captura el personal en el Punto de Venta.
 */
const EstacionFirma: React.FC<{ id: string; modo: Extract<ModoConAcceso, { modo: 'personal' }> }> = ({ id, modo }) => {
    const { vista, cargando, errorCarga, ocupado, aviso, setAviso, ejecutar } = useExpediente(id, modo);

    // El procedimiento a firmar: el del pedido del QR; sin pedido, el pendiente de hoy.
    const renglon = useMemo<ProcedimientoVista | undefined>(() => {
        if (!vista) return undefined;
        if (modo.orderId) return vista.procedimientos.find((p) => p.orderId === modo.orderId);
        return vista.procedimientos.find((p) => !p.firmado && esRenglonDeHoy(p.fecha));
    }, [vista, modo.orderId]);

    if (cargando) return <Cargando />;
    if (errorCarga || !vista) return <ErrorCarga mensaje={errorCarga?.mensaje} estado={errorCarga?.estado} />;

    const primerNombre = vista.clienta.nombre.split(' ')[0];
    const contratoFirmado = vista.consentimiento.firmado;
    const renglonPendiente = !!renglon && !renglon.firmado;
    const todoFirmado = contratoFirmado && !renglonPendiente;

    if (vista.status === 'void') {
        return (
            <Stack spacing={2.25} sx={{ maxWidth: 640, mx: 'auto', pb: 4 }}>
                <Cabecera folio={vista.folio} titulo="Expediente anulado" logo />
                <Alert severity="warning">
                    {vista.anulado?.motivo ? conPunto(vista.anulado.motivo) : 'Este expediente ya no está vigente.'}
                </Alert>
            </Stack>
        );
    }

    return (
        <Stack spacing={2.25} sx={{ maxWidth: 640, mx: 'auto', pb: 4 }}>
            <Cabecera
                logo
                folio={vista.folio}
                titulo={todoFirmado ? `Gracias, ${primerNombre}` : `Hola, ${primerNombre}`}
                subtitulo={
                    todoFirmado
                        ? undefined
                        : renglonPendiente && !contratoFirmado
                          ? 'Son dos firmas: tu contrato y el procedimiento de hoy.'
                          : renglonPendiente
                            ? 'Falta tu firma del procedimiento de hoy.'
                            : 'Falta tu firma en el contrato.'
                }
            />

            {todoFirmado ? (
                <Listo
                    titulo="¡Listo! Todo quedó firmado"
                    detalle="Ya puedes regresar el dispositivo. Tu expediente queda guardado en Kabandú Studio."
                />
            ) : (
                <>
                    <Seccion
                        numero={1}
                        hecho={contratoFirmado}
                        titulo="Tu contrato"
                        detalle={
                            contratoFirmado
                                ? vista.consentimiento.firmadoEl
                                    ? conPunto(`Firmado el ${fechaHora(vista.consentimiento.firmadoEl)}`)
                                    : 'Firmado.'
                                : 'Léelo y firma al final, en el recuadro.'
                        }
                    >
                        {!contratoFirmado && (
                            <FirmaConsentimiento
                                vista={vista}
                                firmando={ocupado === 'contrato'}
                                onFirmar={(firma) =>
                                    ejecutar('contrato', () => ConsentApi.firmarConsentimiento(modo, id, firma))
                                }
                            />
                        )}
                    </Seccion>

                    {renglon && (
                        <Seccion
                            numero={2}
                            hecho={renglon.firmado}
                            apagada={!contratoFirmado}
                            titulo="Tu procedimiento de hoy"
                            detalle={
                                contratoFirmado
                                    ? 'Revisa los datos y firma antes de empezar.'
                                    : 'Primero firma tu contrato.'
                            }
                        >
                            {contratoFirmado && !renglon.firmado && (
                                <FirmaProcedimiento
                                    renglon={renglon}
                                    declaracion={vista.declaracionRenglon || DECLARACION_DEFAULT}
                                    firmando={ocupado === 'renglon'}
                                    onFirmar={(firma) =>
                                        ejecutar('renglon', () =>
                                            ConsentApi.firmarRenglon(modo, id, renglon.orderId, {
                                                firma,
                                                desechable: renglon.desechable || '',
                                                pigmento: renglon.pigmento || ''
                                            })
                                        )
                                    }
                                />
                            )}
                        </Seccion>
                    )}
                </>
            )}

            <AvisoSnackbar aviso={aviso} onClose={() => setAviso(null)} />
        </Stack>
    );
};

/** Datos del procedimiento (los capturó el personal), la declaración y la firma. */
const FirmaProcedimiento: React.FC<{
    renglon: ProcedimientoVista;
    declaracion: string;
    firmando: boolean;
    onFirmar: (firma: string) => void;
}> = ({ renglon, declaracion, firmando, onFirmar }) => {
    const pad = useRef<SignatureFieldHandle>(null);
    const [acepto, setAcepto] = useState(false);
    const [vacio, setVacio] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const firmar = () => {
        setError(null);
        const firma = pad.current?.toPng();
        if (!firma) {
            setError('Firma en el recuadro.');
            return;
        }
        onFirmar(firma);
    };

    return (
        <Stack spacing={2}>
            <Box sx={{ borderRadius: 2, p: 1.75, bgcolor: 'action.hover' }}>
                <Stack spacing={0.75}>
                    <Typography fontWeight={700}>{renglon.servicio}</Typography>
                    <Dato etiqueta="Fecha">{renglon.fecha ? fechaHora(renglon.fecha) : 'Hoy'}</Dato>
                    {renglon.especialista && <Dato etiqueta="Artista">{renglon.especialista}</Dato>}
                    {renglon.desechable && <Dato etiqueta="Desechable / recurso">{renglon.desechable}</Dato>}
                    {renglon.pigmento && <Dato etiqueta="Pigmento">{renglon.pigmento}</Dato>}
                    <Dato etiqueta="Precio">{pesos(renglon.precio)}</Dato>
                </Stack>
            </Box>
            <FormControlLabel
                sx={{ alignItems: 'flex-start', mr: 0 }}
                control={<Checkbox checked={acepto} onChange={(e) => setAcepto(e.target.checked)} sx={{ mt: -0.75 }} />}
                label={<Typography variant="body2">{declaracion}</Typography>}
            />
            <SignatureField ref={pad} label="Tu firma" onChange={setVacio} disabled={firmando} />
            {error && <Alert severity="error">{error}</Alert>}
            <Button variant="contained" size="large" onClick={firmar} disabled={firmando || vacio || !acepto}>
                {firmando ? 'Firmando…' : 'Firmar mi procedimiento'}
            </Button>
        </Stack>
    );
};

export default EstacionFirma;
